import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs plugins once at module level
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
import { extractFiltersFunction } from 'src/features/Icp/utils';
import { FeatureItem, FilterValues } from 'src/model';
import AviProfilePic from 'src/assets/manageProfile/avi-profilepic.jpg';
import KCProfilePic from 'src/assets/manageProfile/karan-profilepic.jpeg';
import { featureFlagsInitialState } from 'src/constants/constants';
import { getLinks } from 'src/components/EnhancedTable/components';
import { getICPValue } from 'src/features/settings/modules/icpFiltersConfiguration/IcpListsV3/utils/utilityMethods';
import { AESEncrypt, AESDecrypt } from './encryptDecryptFunction';
import { setCookie, getCookie, removeCookie } from './cookies';

export const generateProfilePicture = (email: string) => {
  const aviEmail = process.env.REACT_APP_AVI_ADDRESS;
  const karanEmail = process.env.REACT_APP_KC_ADDRESS;

  switch (email) {
    case aviEmail:
      return AviProfilePic;

    case karanEmail:
      return KCProfilePic;
    default:
      return '';
  }
};

export function subtractArrays(array1: string[], array2: string[]): string[] {
  return array1.filter((item) => !array2.includes(item));
}

// Company headcount duration columns shown in the table/enrichment UI.
// Headcount Capsicum Enrichment flag swaps the growth window set returned by the API.
export const getCompanyHeadcountFields = (isHeadcountCapsicumEnrichmentEnabled?: boolean): string[] =>
  isHeadcountCapsicumEnrichmentEnabled
    ? ['totalHeadcount', 'headcount3mGrowthCount', 'headcount6mGrowthCount', 'headcount12mGrowthCount']
    : ['totalHeadcount', 'headcount6mGrowthCount', 'headcount12mGrowthCount', 'headcount24mGrowthCount'];

export const getFormatedDate = (date: string, format?: string) => {
  if (format) return dayjs(date).format(format);
  else return dayjs(date).format('DD MMM, YYYY');
};

export function hasNullValues(object: any): boolean {
  return Object.values(object).some((value) => value === null);
}

export function filterObjectsWithoutNullValues(objects: any[]): boolean {
  return !hasNullValues(objects);
}

export function checkDate(dateString: string, userTimezone?: string) {
  const isValidTimezone = userTimezone && dayjs.tz.guess() !== undefined;

  let inputDate: dayjs.Dayjs;
  let currentDate: dayjs.Dayjs;

  if (isValidTimezone && userTimezone) {
    try {
      // Use timezone-aware date calculations
      inputDate = dayjs(dateString).tz(userTimezone);
      currentDate = dayjs().tz(userTimezone);
    } catch (error) {
      // Fallback to existing implementation if timezone is invalid
      inputDate = dayjs(dateString);
      currentDate = dayjs();
    }
  } else {
    // Fallback to existing implementation
    inputDate = dayjs(dateString);
    currentDate = dayjs();
  }

  if (inputDate.isSame(currentDate, 'day')) {
    return 'Today';
  } else if (inputDate.isSame(currentDate.subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  } else {
    return '';
  }
}

export function removeTrailingSlash(inputUrl: string): string {
  if (inputUrl.endsWith('/')) {
    return inputUrl.slice(0, -1); // Remove the trailing slash
  }
  return inputUrl; // No trailing slash, return the original input
}

export function get24HourFormat(time: string) {
  dayjs.extend(customParseFormat);

  return dayjs(time, 'HH:mm:ss A').format('HH:mm');
}

export function extractUTMParams(urlParameters?: string): Record<string, string> {
  const utmParams: Record<string, string> = {};

  if (!urlParameters || urlParameters.trim() === '') {
    return utmParams;
  }

  const params = new URLSearchParams(urlParameters);

  params.forEach((value, key) => {
    if (key.startsWith('utm_') && !utmParams[key]) {
      utmParams[key] = value;
    }
  });

  return utmParams;
}

export function decodeValue(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
}

export function cleanUTMParams(utmParams: Record<string, string>): Record<string, string> {
  const cleanedParams: Record<string, string> = {};

  for (const [key, value] of Object.entries(utmParams)) {
    const newKey = key.replace('utm_', '');
    cleanedParams[newKey] = decodeValue(value);
  }
  const sortedKeys = Object.keys(cleanedParams).sort((a, b) => {
    if (a === 'campaign') return -1;
    if (b === 'campaign') return 1;
    if (a === 'source') return -1;
    if (b === 'source') return 1;
    if (a === 'medium') return -1;
    if (b === 'medium') return 1;
    if (a === 'term') return -1;
    if (b === 'term') return 1;
    if (a === 'content') return -1;
    if (b === 'content') return 1;
    return 0;
  });

  const sortedCleaned: Record<string, string> = {};
  sortedKeys.forEach((key) => {
    sortedCleaned[key] = cleanedParams[key];
  });

  return sortedCleaned;
}

export const formatStoreFilters = (filters: any) => {
  const newAppliedFilters = JSON.parse(JSON.stringify(filters));
  const extractedArray = extractFiltersFunction(newAppliedFilters);
  for (let i = 0; i <= extractedArray.length; i++) {
    if (extractedArray[i]?.range) {
      extractedArray[i].numericRanges = extractedArray[i].range;
      delete extractedArray[i]?.range;
    }
    // Handle Company Headcount filter (single interval/min/max)
    if (extractedArray[i]?.filterName === 'Headcount Growth') {
      if (
        extractedArray[i]?.interval ||
        extractedArray[i]?.min !== undefined ||
        extractedArray[i]?.max !== undefined
      ) {
        extractedArray[i].numericRanges = [
          {
            interval: extractedArray[i].interval || '',
            min: extractedArray[i].min ?? null,
            max: extractedArray[i].max ?? null,
          },
        ];
        delete extractedArray[i].interval;
        delete extractedArray[i].min;
        delete extractedArray[i].max;
      }
      continue;
    }
    if (
      extractedArray[i]?.min ||
      extractedArray[i]?.max ||
      (extractedArray[i]?.filterName === 'Intent Score' &&
        (extractedArray[i]?.min === 0 || extractedArray[i]?.max === 0))
    ) {
      extractedArray[i].valueRange = {
        min:
          extractedArray[i]?.filterName === 'Intent Score' && extractedArray[i].min === 0
            ? 0
            : extractedArray[i].min || null,
        max:
          extractedArray[i]?.filterName === 'Intent Score' && extractedArray[i].max === 0
            ? 0
            : extractedArray[i].max || null,
      };
      if (extractedArray[i]?.min === 'Unavailable') {
        extractedArray[i].valueRange.unavailable = true;
      }
      if (extractedArray[i]?.max === 'Unavailable') {
        extractedArray[i].valueRange.unavailable = true;
      }
      delete extractedArray[i]?.min;
      delete extractedArray[i]?.max;
    }
    if (extractedArray[i]?.fromDate || extractedArray[i]?.toDate) {
      extractedArray[i].dateRange = {
        fromDate: extractedArray[i].fromDate ? convertLocalToUTC(extractedArray[i].fromDate) : null,
        toDate: extractedArray[i].toDate ? convertLocalToUTC(extractedArray[i].toDate) : null,
      };
      delete extractedArray[i]?.fromDate;
      delete extractedArray[i]?.toDate;
    }
    const filterName = extractedArray[i]?.filterName || '';
    const isCompanyTypeFilter =
      filterName.includes('Organization') ||
      filterName.includes('Companies') ||
      filterName.toLowerCase().includes('company');
    const isAccountNameFilter = filterName === 'accountName' || filterName === 'Account Name';
    const shouldTransformToIdArray = isCompanyTypeFilter || isAccountNameFilter;

    if (extractedArray[i]?.includes) {
      if (shouldTransformToIdArray && Array.isArray(extractedArray[i].includes)) {
        extractedArray[i].includes = extractedArray[i].includes.map((item: any) => {
          if (typeof item === 'object' && item !== null) {
            return item.id || item.domain || item;
          }
          return item;
        });
      }
      replaceUnavailableValues(extractedArray[i].includes);
    }
    if (extractedArray[i]?.excludes) {
      if (shouldTransformToIdArray && Array.isArray(extractedArray[i].excludes)) {
        extractedArray[i].excludes = extractedArray[i].excludes.map((item: any) => {
          if (typeof item === 'object' && item !== null) {
            return item.id || item.domain || item;
          }
          return item;
        });
      }
      replaceUnavailableValues(extractedArray[i].excludes);
    }
  }
  return extractedArray;
};

export function hasPermission(
  ownerEmail: string,
  userEmail: string,
  permissions: string[],
): boolean {
  // If the user has the "Everyone" permission, they can edit all .
  if (permissions.includes('Everyone')) {
    return true;
  }
  // If the user has the "Self" permission, they can only edit their own.
  else if (permissions.includes('Self') && ownerEmail === userEmail) {
    return true;
  }
  // If the user has the "Unassigned" permission, they can edit that are unassigned.
  else if (permissions.includes('Unassigned') && ownerEmail === '') {
    return true;
  }
  // If none of the conditions are met, the user cannot edit.
  // this case should not occur
  return false;
}

export const hasSettingsPermission = (
  permissions: string[],
  type?: 'view' | 'edit' | 'no-access',
) => {
  if (type === 'view' && Array.isArray(permissions))
    return permissions.includes('Can view') || permissions.includes('Can edit');
  if (type === 'edit' && Array.isArray(permissions)) return permissions.includes('Can edit');
  return false;
};

function replaceUnavailableValues(array: string[] | undefined): void {
  if (array) {
    // Check if values include 'Unavailable'
    for (let i = 0; i < array.length; i++) {
      if (array[i] === 'Unavailable') {
        array[i] = '';
      }
    }
  }
}

export function convertLocalToUTC(dateString: string): string {
  // Use dayjs with UTC plugin to properly convert local time to UTC
  dayjs.extend(utc);

  // Parse the input date in local timezone
  const localDate = dayjs(dateString);

  // Convert to UTC and format to the expected format
  return localDate.utc().format('YYYY-MM-DD HH:mm:ss');
}

export const capitalizeFirstLetterInAllWords = (inputString: string | number | null) => {
  if (typeof inputString === 'string') {
    return inputString?.toLowerCase()?.replace(/\b\w/g, function (match) {
      return match.toUpperCase();
    });
  }
  return inputString;
};

export const capitalizeFirstLetter = (inputString: string) => {
  if (typeof inputString !== 'string') {
    // Check if the input is a string; if not, return the input as is.
    return inputString;
  }

  if (inputString.length === 0) {
    // Handle empty strings by returning an empty string.
    return '';
  }

  // Capitalize the first character and concatenate it with the rest of the string.
  return inputString.charAt(0).toUpperCase() + inputString.slice(1);
};

export function decodeQueryParamString(queryParam: string): string {
  // Use decodeURIComponent to replace %20 with space
  const decodedString = decodeURIComponent(queryParam.replace(/\+/g, ' '));

  return decodedString;
}

export function convertToDayStart(inputDate: Date) {
  inputDate.setHours(0, 0, 0, 0);
  return inputDate;
}
export function convertToDayEnd(inputDate: Date) {
  inputDate.setHours(23, 59, 59, 999);
  return inputDate;
}
export const isEmail = (value: string) =>
  /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value);
export const isLongLink = (value: string) =>
  /(http|ftp|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])/.test(
    value,
  );

export function removeHttpWww(url: string): string {
  if (url) return url.replace(/(https?:\/\/)?www\./i, '');
  else return '';
}

export const getCompanyLogo = (domain?: string, logoUrl?: string): string => {
  if (logoUrl && logoUrl !== 'null') return /^https?:\/\//i.test(logoUrl) ? logoUrl : `https://${logoUrl}`;
  return domain ? `https://logo.sprouts.ai/logo/${domain}` : '';
};
export function updatePageSize(url: string, pageSize: number, term = 'page_size'): string {
  const urlObject = new URL(url);

  // Check if the URL already has a pageSize query parameter
  if (urlObject.searchParams.has(term)) {
    // Update the existing pageSize value
    urlObject.searchParams.set(term, pageSize.toString());
  } else {
    // Add pageSize query parameter if not present
    urlObject.searchParams.append(term, pageSize.toString());
  }

  return urlObject.toString();
}

export const validatingPhoneNumber = (inputtxt: string) => {
  const phonenoRegEx = /^(?!.*\+.*\+)[+\d()]+[-\d()+ ]{0,63}$/;
  return phonenoRegEx.test(inputtxt);
};

export const validatingPhoneNumberV2 = (inputtxt: string) => {
  // follows rules.
  // + in first only
  // allow spaces but no more one consecutive
  // only numbers are allowed.
  const phonenoRegEx = /^\+?$|^\+?\d+(\s?\d+)*\s?$/;
  return phonenoRegEx.test(inputtxt);
};

export const validateNumber = (inputtxt: string) => {
  return /^-?\d*\.?\d*$/.test(inputtxt);
};

export const formatDateToMMDD = (date: Date | string | number): string => {
  const dateObj = new Date(date);

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getDate().toString().padStart(2, '0');

  return `${day}/${month}`;
};

export function formatDate(inputDate: string) {
  const parsedDate = new Date(inputDate);
  if (isNaN(parsedDate.getTime())) {
    return 'Invalid Date';
  }

  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  const formattedDate = parsedDate.toLocaleDateString('en-GB', options);
  return formattedDate?.replaceAll(' ', ', ')?.replace(',', '');
}

export function formatDateAndTime(inputDate: string) {
  // Parse the input date string
  const date = new Date(inputDate);

  // Month names
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Get date components
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const hours = ('0' + date.getHours()).slice(-2);
  const minutes = ('0' + date.getMinutes()).slice(-2);

  // Format the date
  const formattedDate = `${day} ${month} ${year}, ${hours}:${minutes}`;

  return formattedDate;
}

export function customSortColumns(a: any, b: any) {
  const desiredTitles = [
    'Contact Name',
    'Account Name',
    'Account ICP',
    'Miller Heiman Role',
    'Connection Path',
    'AI News',
    'Awards and Recognitions',
    'Recent Funding',
    'AI Questions',
    'Designation',
    'Department',
    'Job Functions',
    'Hiring',
    'Job Opening Source',
  ];
  const indexA = desiredTitles.indexOf(a.title);
  const indexB = desiredTitles.indexOf(b.title);

  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  } else if (indexA !== -1) {
    return -1;
  } else if (indexB !== -1) {
    return 1;
  } else {
    return 0;
  }
}

export function calculateMaxBadges(words: string | string[], threshold: number) {
  let remainingThreshold = threshold;
  let maxBadges = 0;

  if (!words || words.length === 0 || words[0].length > threshold) {
    maxBadges = 1;
  } else {
    for (let i = 0; i < words.length; i++) {
      const wordLength = words[i].length;

      if (wordLength <= remainingThreshold) {
        remainingThreshold -= wordLength + 1;
        maxBadges++;
      } else {
        break;
      }
    }
  }

  return maxBadges;
}
export function areArraysEqualTargetProfiles(array1: any[], array2: any[]): boolean {
  // Check if the lengths of both arrays are equal
  if (array1.length !== array2.length) {
    return false;
  }

  // Sort arrays based on the 'id' property
  const sortedArray1 = array1
    .slice()
    .sort((a, b) => `${a.primaryKey}`.localeCompare(`${b.primaryKey}`));
  const sortedArray2 = array2
    .slice()
    .sort((a, b) => `${a.primaryKey}`.localeCompare(`${b.primaryKey}`));

  // Compare each object in the sorted arrays
  for (let i = 0; i < sortedArray1.length; i++) {
    if (sortedArray1[i].primaryKey !== sortedArray2[i].primaryKey) {
      return false;
    }

    // You can add additional checks for other properties if needed
    // For example:
    // if (sortedArray1[i].someProperty !== sortedArray2[i].someProperty) {
    //   return false;
    // }
  }

  // All objects are equal
  return true;
}

export function areAllItemsInArray1PresentInArray2(
  array1: FilterValues,
  array2: FilterValues,
): boolean {
  if (array1.length === 0) {
    return false;
  }
  // Create a Set of unique items from array2
  const setFromArray2 = new Set(array2.map((item) => item.id));

  // Check if all items from array1 are present in setFromArray2
  const output = array1.every((item) => setFromArray2.has(item.id));
  return output;
}

export function indeterminateStateCheck(array1: FilterValues, array2: FilterValues): boolean {
  // Create a Set of unique items from array1
  const setFromArray1 = new Set(array1.map((item) => item.id));

  // Check if any item from array2 is present in setFromArray1
  return array2.some((item) => setFromArray1.has(item.id));
}

export function getDomainFromEmail(email: string): string | null {
  // Check if the email contains '@' and split the string by '@'
  const parts = email.split('@');

  // If the email contains exactly one '@' and has a domain part, return the domain
  if (parts.length === 2 && parts[1]) {
    return parts[1];
  }

  // If the email is not valid, return null
  return null;
}

export const transformFeaturesForRedux = (features: FeatureItem[]) => {
  const newFeaturesData = { ...featureFlagsInitialState };
  const newAdminFeaturesData = { ...featureFlagsInitialState };
  features.forEach((item) => {
    switch (item.feature) {
      case 'Display Opportunity on Contact':
      case 'Display opportunity on contact':
      case 'display opportunity on contact':
        newFeaturesData.showOpportunities = item.enabled ? true : false;
        newAdminFeaturesData.showOpportunities = item?.isSproutsAdmin ? true : false;
        break;
      // Settings -> Configurations -> API Keys. Several spellings because the switch
      // matches the backend's display name exactly, and this list is the only place
      // that coupling is visible.
      case 'API Keys':
      case 'Api Keys':
      case 'API keys':
      case 'api keys':
        newFeaturesData.apiKeys = item.enabled ? true : false;
        newAdminFeaturesData.apiKeys = item?.isSproutsAdmin ? true : false;
        break;
      case 'Automated Playbook':
        newFeaturesData.automated_playbook = item.enabled ? true : false;
        newAdminFeaturesData.automated_playbook = item?.isSproutsAdmin ? true : false;
        break;
      case 'New PlayBook':
        newFeaturesData.newPlaybook = item.enabled ? true : false;
        newAdminFeaturesData.newPlaybook = item?.isSproutsAdmin ? true : false;
        break;
      case 'Website Visitors Dashboard':
        newFeaturesData.websiteVisitorDashboard = item.enabled ? true : false;
        newAdminFeaturesData.websiteVisitorDashboard = item?.isSproutsAdmin ? true : false;
        break;
      case 'Website Tracking':
        newFeaturesData.websiteAccountList = item.enabled ? true : false;
        newAdminFeaturesData.websiteAccountList = item?.isSproutsAdmin ? true : false;
        break;
      case 'Campaign Dashboard':
        newFeaturesData.campaignDashboard = item.enabled ? true : false;
        newAdminFeaturesData.campaignDashboard = item?.isSproutsAdmin ? true : false;
        break;
      case 'Hubspot Integration':
        newFeaturesData.hubspot = item.enabled ? true : false;
        newAdminFeaturesData.hubspot = item?.isSproutsAdmin ? true : false;
        break;
      case 'Zoho Integration':
        newFeaturesData.zoho = item.enabled ? true : false;
        newAdminFeaturesData.zoho = item?.isSproutsAdmin ? true : false;
        break;
      case 'Freshsales Integration':
        newFeaturesData.freshsales = item.enabled ? true : false;
        newAdminFeaturesData.freshsales = item?.isSproutsAdmin ? true : false;
        break;
      case 'Salesforce Integration':
        newFeaturesData.salesforce = item.enabled ? true : false;
        newAdminFeaturesData.salesforce = item?.isSproutsAdmin ? true : false;
        break;
      case 'Pipedrive Integration':
        newFeaturesData.pipedrive = item.enabled ? true : false;
        newAdminFeaturesData.pipedrive = item?.isSproutsAdmin ? true : false;
        break;
      case 'Outreach Integration':
        newFeaturesData.outreach = item.enabled ? true : false;
        newAdminFeaturesData.outreach = item?.isSproutsAdmin ? true : false;
        break;
      case 'Microsoft Integration':
        newFeaturesData.microsoft = item.enabled ? true : false;
        newAdminFeaturesData.microsoft = item?.isSproutsAdmin ? true : false;
        break;
      case 'Pardot Integration':
        newFeaturesData.pardot = item.enabled ? true : false;
        newAdminFeaturesData.pardot = item?.isSproutsAdmin ? true : false;
        break;
      case 'Gong Integration':
        newFeaturesData.gong = item.enabled ? true : false;
        newAdminFeaturesData.gong = item?.isSproutsAdmin ? true : false;
        break;
      case 'Salesloft Integration':
        newFeaturesData.salesloft = item.enabled ? true : false;
        newAdminFeaturesData.salesloft = item?.isSproutsAdmin ? true : false;
        break;
      case 'Instantly Integration':
        newFeaturesData.instantly = item.enabled ? true : false;
        newAdminFeaturesData.instantly = item?.isSproutsAdmin ? true : false;
        break;
      case 'Mailchimp Integration':
        newFeaturesData.mailchimp = item.enabled ? true : false;
        newAdminFeaturesData.mailchimp = item?.isSproutsAdmin ? true : false;
        break;
      case 'Heyreach Integration':
        newFeaturesData.heyreach = item.enabled ? true : false;
        newAdminFeaturesData.heyreach = item?.isSproutsAdmin ? true : false;
        break;
      case 'Intercom Integration':
        newFeaturesData.intercom = item.enabled ? true : false;
        newAdminFeaturesData.intercom = item?.isSproutsAdmin ? true : false;
        break;
      case 'Intercom Messenger':
        newFeaturesData.intercomMessenger = item.enabled ? true : false;
        newAdminFeaturesData.intercomMessenger = item?.isSproutsAdmin ? true : false;
        break;
      case 'Intercom Messenger Integration':
        // Backend naming variant for the Intercom support widget flag
        newFeaturesData.intercomMessenger = item.enabled ? true : false;
        newAdminFeaturesData.intercomMessenger = item?.isSproutsAdmin ? true : false;
        break;
      case 'Lemlist Integration':
        newFeaturesData.lemlist = item.enabled ? true : false;
        newAdminFeaturesData.lemlist = item?.isSproutsAdmin ? true : false;
        break;
      case 'Gmail Integration':
        newFeaturesData.gmail = item.enabled ? true : false;
        newAdminFeaturesData.gmail = item?.isSproutsAdmin ? true : false;
        break;
      case 'Google Drive Integration':
        newFeaturesData.googledrive = item.enabled ? true : false;
        newAdminFeaturesData.googledrive = item.isSproutsAdmin ? true : false;
        break;
      case 'Google Slides Integration':
        newFeaturesData.googleslides = item.enabled ? true : false;
        newAdminFeaturesData.googleslides = item.isSproutsAdmin ? true : false;
        break;
      case 'Google Docs Integration':
        newFeaturesData.googledocs = item.enabled ? true : false;
        newAdminFeaturesData.googledocs = item.isSproutsAdmin ? true : false;
        break;
      case 'Smartlead Integration':
        newFeaturesData.smartlead = item.enabled ? true : false;
        newAdminFeaturesData.smartlead = item.isSproutsAdmin ? true : false;
        break;
      case 'Slack Integration':
        newFeaturesData.slack = item.enabled ? true : false;
        newAdminFeaturesData.slack = item?.isSproutsAdmin ? true : false;
        break;
      case 'Amplitude Integration':
        newFeaturesData.amplitude = item.enabled ? true : false;
        newAdminFeaturesData.amplitude = item?.isSproutsAdmin ? true : false;
        break;
      case 'Segment Integration':
        newFeaturesData.segment = item.enabled ? true : false;
        newAdminFeaturesData.segment = item?.isSproutsAdmin ? true : false;
        break;
      case 'Mixpanel Integration':
        newFeaturesData.mixpanel = item.enabled ? true : false;
        newAdminFeaturesData.mixpanel = item?.isSproutsAdmin ? true : false;
        break;
      case 'G2Crowd Integration':
        newFeaturesData.g2crowd = item.enabled ? true : false;
        newAdminFeaturesData.g2crowd = item?.isSproutsAdmin ? true : false;
        break;
      case 'Trustradius Integration':
        newFeaturesData.trustradius = item.enabled ? true : false;
        newAdminFeaturesData.trustradius = item?.isSproutsAdmin ? true : false;
        break;
      case 'Google Analytics Integration':
        newFeaturesData.googleanalytics = item.enabled ? true : false;
        newAdminFeaturesData.googleanalytics = item?.isSproutsAdmin ? true : false;
        break;
      case 'Swarm Integration':
        newFeaturesData.swarm = item.enabled ? true : false;
        newAdminFeaturesData.swarm = item?.isSproutsAdmin ? true : false;
        break;
      case 'Outlook Integration':
        newFeaturesData.outlook = item.enabled ? true : false;
        newAdminFeaturesData.outlook = item.isSproutsAdmin ? true : false;
        break;
      case 'Teams Integration':
        newFeaturesData.teams = item.enabled ? true : false;
        newAdminFeaturesData.teams = item.isSproutsAdmin ? true : false;
        break;
      case 'SendGrid Integration':
        newFeaturesData.sendgrid = item.enabled ? true : false;
        newAdminFeaturesData.sendgrid = item.isSproutsAdmin ? true : false;
        break;
      case 'Pendo Integration':
        newFeaturesData.pendo = item.enabled ? true : false;
        newAdminFeaturesData.pendo = item.isSproutsAdmin ? true : false;
        break;
      case 'Adobe Marketo Integration':
        newFeaturesData.adobemarketo = item.enabled ? true : false;
        newAdminFeaturesData.adobemarketo = item.isSproutsAdmin ? true : false;
        break;
      case 'SimilarWeb Integration':
        newFeaturesData.similarweb = item.enabled ? true : false;
        newAdminFeaturesData.similarweb = item.isSproutsAdmin ? true : false;
        break;
      case 'Semrush Integration':
        newFeaturesData.semrush = item.enabled ? true : false;
        newAdminFeaturesData.semrush = item.isSproutsAdmin ? true : false;
        break;
      case 'Mock Integration':
        newFeaturesData.mockIntegration = item.enabled ? true : false;
        newAdminFeaturesData.mockIntegration = item?.isSproutsAdmin ? true : false;
        break;
      case 'Account Scoring - ICP (v0.1)':
        newFeaturesData.icpScoring = item.enabled ? true : false;
        newAdminFeaturesData.icpScoring = item?.isSproutsAdmin ? true : false;
        break;
      case 'Chatbot':
        newFeaturesData.chatbot = item.enabled ? true : false;
        newAdminFeaturesData.chatbot = item?.isSproutsAdmin ? true : false;
        break;
      case 'AI News':
        newFeaturesData.aiNews = item?.enabled ? true : false;
        newAdminFeaturesData.aiNews = item?.isSproutsAdmin ? true : false;
        break;
      case 'Recent Funding':
        newFeaturesData.recentFunding = item?.enabled ? true : false;
        newAdminFeaturesData.recentFunding = item?.isSproutsAdmin ? true : false;
        break;
      case 'Awards and Recognition':
        newFeaturesData.awardsAndRecognition = item?.enabled ? true : false;
        newAdminFeaturesData.awardsAndRecognition = item?.isSproutsAdmin ? true : false;
        break;
      case 'Review on G2':
        newFeaturesData.g2_enabled = item?.enabled ? true : false;
        newAdminFeaturesData.g2_enabled = item?.isSproutsAdmin ? true : false;
        break;
      case 'Contact Reveal':
        newFeaturesData.contactReveal = item.enabled ? true : false;
        newAdminFeaturesData.contactReveal = item.isSproutsAdmin ? true : false;
        break;
      case 'Custom AI Signals':
        newFeaturesData.customAiSignals = item.enabled ? true : false;
        newAdminFeaturesData.customAiSignals = item.isSproutsAdmin ? true : false;
        break;
      case 'Job Opening Signals':
        newFeaturesData.jobOpening = item.enabled ? true : false;
        newAdminFeaturesData.jobOpening = item.isSproutsAdmin ? true : false;
        break;
      case 'Data Enrichment':
        if (item.enabled) {
          localStorage.setItem('dataEnrichmentFeature', JSON.stringify(item));
        } else {
          localStorage.removeItem('dataEnrichmentFeature');
        }
        break;
      case 'Multi Child Functionality':
        newFeaturesData.multiChild = item.enabled ? true : false;
        newAdminFeaturesData.multiChild = item.isSproutsAdmin ? true : false;
        break;
      case 'Hybrid Custom Manual ICP Scoring':
        newFeaturesData.hybridCustomIcp = item.enabled ? true : false;
        newAdminFeaturesData.hybridCustomIcp = item.isSproutsAdmin ? true : false;
        break;
      case 'RealTime Account Scoring - ICP':
        newFeaturesData.realTimeIcp = item.enabled ? true : false;
        newAdminFeaturesData.realTimeIcp = item.isSproutsAdmin ? true : false;
        break;
      case 'Job Change Alert':
        newFeaturesData.jobChangeAlert = item.enabled ? true : false;
        newAdminFeaturesData.jobChangeAlert = item.isSproutsAdmin ? true : false;
        break;
      case 'Hybrid Custom Manual Weightage ICP Scoring':
        newFeaturesData.hybridCustomWeightageIcp = item.enabled ? true : false;
        newAdminFeaturesData.hybridCustomWeightageIcp = item.isSproutsAdmin ? true : false;
        break;
      case 'Dummy Domain':
        newFeaturesData.dummyDomain = item.enabled ? true : false;
        newAdminFeaturesData.dummyDomain = item.isSproutsAdmin ? true : false;
        break;
      case 'Champion Tracking':
        newFeaturesData.championTracking = item.enabled ? true : false;
        newAdminFeaturesData.championTracking = item.isSproutsAdmin ? true : false;
        break;
      case 'Display Lead Sync':
        newFeaturesData.displayLeadSync = item.enabled ? true : false;
        newAdminFeaturesData.displayLeadSync = item.isSproutsAdmin ? true : false;
        break;
      case 'Validate Email':
        newFeaturesData.validateEmail = item.enabled ? true : false;
        newAdminFeaturesData.validateEmail = item.isSproutsAdmin ? true : false;
        break;
      case 'Validate PhoneNumber':
        newFeaturesData.validatePhoneNumber = item.enabled ? true : false;
        newAdminFeaturesData.validatePhoneNumber = item.isSproutsAdmin ? true : false;
        break;
      case 'HPM - Custom Configuration':
        newFeaturesData.hpmCustomConfiguration = item.enabled ? true : false;
        newAdminFeaturesData.hpmCustomConfiguration = item.isSproutsAdmin ? true : false;
        break;
      case 'HPM - Manual ICP':
        newFeaturesData.hpmManualIcp = item.enabled ? true : false;
        newAdminFeaturesData.hpmManualIcp = item.isSproutsAdmin ? true : false;
        break;
      case 'Move To TAL over Kafka':
        newFeaturesData.moveToTalOverKafka = item.enabled ? true : false;
        newAdminFeaturesData.moveToTalOverKafka = item.isSproutsAdmin ? true : false;
        break;
      case 'Miller Heiman Roles':
        newFeaturesData.millerHeimanRoles = item.enabled ? true : false;
        newAdminFeaturesData.millerHeimanRoles = item.isSproutsAdmin ? true : false;
        break;
      case 'Engagement Scores':
        newFeaturesData.engagementScores = item.enabled ? true : false;
        newAdminFeaturesData.engagementScores = item.isSproutsAdmin ? true : false;
        break;
      case 'AI Signal - Auto Refresh':
        newFeaturesData.aiSignalAutoRefresh = item.enabled ? true : false;
        newAdminFeaturesData.aiSignalAutoRefresh = item.isSproutsAdmin ? true : false;
        break;
      case 'Self Recharge':
        newFeaturesData.selfRecharge = item.enabled ? true : false;
        newAdminFeaturesData.selfRecharge = item.isSproutsAdmin ? true : false;
        break;
      case 'Enrichment By Name Linkedin':
        newFeaturesData.enrichmentByNameLinkedin = item.enabled ? true : false;
        newAdminFeaturesData.enrichmentByNameLinkedin = item.isSproutsAdmin ? true : false;
        break;
      case 'Conversational Playbook':
        newFeaturesData.conversationalPlaybook = item.enabled ? true : false;
        newAdminFeaturesData.conversationalPlaybook = item.isSproutsAdmin ? true : false;
        break;
      case 'Account intelligence':
        newFeaturesData.accountIntelligence = item.enabled ? true : false;
        newAdminFeaturesData.accountIntelligence = item.isSproutsAdmin ? true : false;
        break;
      case 'Real Time Data Refresh':
        newFeaturesData.realTimeDataRefresh = item.enabled ? true : false;
        newAdminFeaturesData.realTimeDataRefresh = item.isSproutsAdmin ? true : false;
        break;
      case 'Ask AI 2.0':
        newFeaturesData.askAi2 = item.enabled ? true : false;
        newAdminFeaturesData.askAi2 = item.isSproutsAdmin ? true : false;
        break;
      case 'Sales Nav Import':
        newFeaturesData.salesNavImport = item.enabled ? true : false;
        newAdminFeaturesData.salesNavImport = item.isSproutsAdmin ? true : false;
        break;
      case 'Linkedin Fake Profile Validation':
        newFeaturesData.linkedinFakeProfileValidation = item.enabled ? true : false;
        newAdminFeaturesData.linkedinFakeProfileValidation = item.isSproutsAdmin ? true : false;
        break;
      case 'User Level Credit':
        newFeaturesData.userLevelCredit = item.enabled ? true : false;
        newAdminFeaturesData.userLevelCredit = item.isSproutsAdmin ? true : false;
        break;
      case 'Notifications':
        newFeaturesData.notifications = item.enabled ? true : false;
        newAdminFeaturesData.notifications = item.isSproutsAdmin ? true : false;
        break;
      case 'Discover and Refresh':
        newFeaturesData.discoverAndRefresh = item.enabled ? true : false;
        newAdminFeaturesData.discoverAndRefresh = item.isSproutsAdmin ? true : false;
        break;
      case 'Tomato Deep Chunking':
        newFeaturesData.tomatoDeepChunking = item.enabled ? true : false;
        newAdminFeaturesData.tomatoDeepChunking = item.isSproutsAdmin ? true : false;
        break;
      case 'Custom Field Extension':
        newFeaturesData.customFieldExtension = item.enabled ? true : false;
        newAdminFeaturesData.customFieldExtension = item.isSproutsAdmin ? true : false;
        break;
      case 'Domain filtering':
        newFeaturesData.domainFiltering = item.enabled ? true : false;
        newAdminFeaturesData.domainFiltering = item.isSproutsAdmin ? true : false;
        break;
      case 'Headcount Capsicum Enrichment': 
        newFeaturesData.headcountCapsicumEnrichment = item.enabled ? true : false;
        newAdminFeaturesData.headcountCapsicumEnrichment = item.isSproutsAdmin ? true : false;
        break;
      default:
        break;
    }
  });
  return { features: newFeaturesData, adminFeatures: newAdminFeaturesData };
};

export function formatNumberToBytes(bytes: number | null) {
  if (bytes !== null) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export function formatNumberToAbbreviations(num: number | string): string {
  let parsedNum: number;

  // Convert string to number if input is a string
  if (typeof num === 'string') {
    parsedNum = parseFloat(num);
  } else {
    parsedNum = num;
  }

  if (isNaN(parsedNum)) {
    return 'Invalid input';
  }

  // Round to the nearest thousand or million
  if (parsedNum >= 1000000) {
    parsedNum = Math.round(parsedNum / 100000) / 10; // Round to one decimal place for millions
    return `${parsedNum?.toLocaleString()}M`;
  } else if (parsedNum >= 1000) {
    parsedNum = Math.round(parsedNum / 100) / 10; // Round to one decimal place for thousands
    return `${parsedNum?.toLocaleString()}K`;
  } else {
    return parsedNum?.toLocaleString(); // Display comma-separated format for numbers less than 1000
  }
}

export function formatNumberToAbbreviationsExtended(num: number | string): string {
  let parsedNum: number;

  // Convert string to number if input is a string
  if (typeof num === 'string') {
    parsedNum = parseFloat(num);
  } else {
    parsedNum = num;
  }

  if (isNaN(parsedNum)) {
    return 'Invalid input';
  }

  // Round to the nearest thousand or million
  if (parsedNum >= 1000000000) {
    parsedNum = Math.round(parsedNum / 100000000) / 10; // Round to one decimal place for billions
    return `${parsedNum?.toLocaleString()}B`;
  } else if (parsedNum >= 1000000) {
    parsedNum = Math.round(parsedNum / 100000) / 10; // Round to one decimal place for millions
    return `${parsedNum?.toLocaleString()}M`;
  } else if (parsedNum >= 1000) {
    parsedNum = Math.round(parsedNum / 100) / 10; // Round to one decimal place for thousands
    return `${parsedNum?.toLocaleString()}K`;
  } else {
    return parsedNum?.toLocaleString(); // Display comma-separated format for numbers less than 1000
  }
}

export function formatNumberToCurrency(amount: number): string {
  if (amount >= 1e12) {
    return '$' + Math.round(amount / 1e12) + 'T';
  } else if (amount >= 1e9) {
    return '$' + Math.round(amount / 1e9) + 'B';
  } else if (amount >= 1e6) {
    return '$' + Math.round(amount / 1e6) + 'M';
  } else if (amount >= 1e3) {
    return '$' + Math.round(amount / 1e3) + 'K';
  } else if (amount === 0) {
    return '$' + 0;
  } else {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
}

export function addCommasToNumber(num: number | string): string {
  let parsedNum: number;

  // Convert string to number if input is a string
  if (typeof num === 'string') {
    parsedNum = parseFloat(num);
  } else {
    parsedNum = num;
  }

  if (isNaN(parsedNum)) {
    return 'Invalid input';
  }

  // Add commas to the number
  return parsedNum?.toLocaleString();
}

const isUnicodeShortLink = (url: string): boolean => {
  if (!url || url.startsWith('http') || !url.includes('.')) return false;
  try {
    return new URL(`https://${url}`).hostname.includes('.');
  } catch {
    return false;
  }
};

export const fixSocialLinks = (
  url: string,
  isAccount: boolean,
  type: 'linkedin' | 'twitter' | 'facebook' | 'website',
) => {
  const { isShortLink } = getLinks(url);
  if (isLongLink(url)) {
    return url;
  } else if (isShortLink || isUnicodeShortLink(url)) {
    if (url?.split('.')?.[0] === 'www') {
      return `https://${url}`;
    } else {
      return `https://www.${url}`;
    }
  } else {
    if (typeof url === 'string' && url != 'null' && url?.length > 0) {
      if (type === 'linkedin') {
        return `https://www.linkedin.com/${isAccount ? 'company' : 'in'}/${url}`;
      } else if (type === 'twitter') {
        return `https://twitter.com/${url}`;
      } else if (type === 'facebook') {
        return `https://www.facebook.com/${url}`;
      } else if (type === 'website') {
        if (url.split('.')[0] === 'www') {
          return `https://${url}`;
        } else {
          return `https://www.${url}`;
        }
      }
    } else {
      return url;
    }
  }
};

export const firstnameRegEx = /^(?!-)[a-zA-Z-]{1,50}$/;
export const lastnameRegEx = /^(?!-)[a-zA-Z-]{1,50}\.?$/;
export const jobTitleRegEx = /^[a-zA-Z ]{1,125}$/;

export const emailRegex = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
export const contactLinkedInRegEx = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
export const contactLinkedInRegExV2 =
  /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company)\/[a-zA-Z0-9-]+\/?$/;
export const domainNameRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+\/?$/;

export const normalizeDomain = (input: string): string => {
  return input
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '');
};

export const isValidDomain = (domain: string) => {
  if (!domainNameRegex.test(domain)) {
    return false;
  }

  // Split the domain into parts using the dot (.) as the separator
  const parts = domain.split('.');

  // Ensure that no part of the domain starts or ends with a hyphen
  return parts.every((part) => !part.startsWith('-') && !part.endsWith('-'));
};

export const copyToClipboard = async (
  selectedData: string,
  toolTipToggle: (val: boolean) => void,
) => {
  try {
    await navigator.clipboard.writeText(selectedData);
    toolTipToggle(true);
    setTimeout(() => toolTipToggle(false), 2000);
  } catch (err) {
    console.log(err);
    toolTipToggle(true);
    const textField = document.createElement('textarea');
    textField.innerText = selectedData;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand('copy');
    textField.remove();
    setTimeout(() => toolTipToggle(false), 2000);
  }
};

export const copyToClipboardIndexBased = async (
  selectedData: string,
  toolTipToggle: (val: boolean) => void,
) => {
  try {
    await navigator.clipboard.writeText(selectedData);
    toolTipToggle(true);
    setTimeout(() => toolTipToggle(false), 2000);
  } catch (err) {
    console.log(err);
    toolTipToggle(true);
    const textField = document.createElement('textarea');
    textField.innerText = selectedData;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand('copy');
    textField.remove();
    setTimeout(() => toolTipToggle(false), 2000);
  }
};

export const isValidURL = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch (_) {
    return false;
  }
};

export const scrollToTop = (elementId: string, offsetFromTop = 0) => {
  const element = document.getElementById(elementId);
  if (element) {
    const topOffset = element.offsetTop - offsetFromTop;
    element.parentElement?.scrollTo({
      top: topOffset,
      behavior: 'smooth',
    });
  }
};

export const getDomainfromUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    let domain = parsedUrl.hostname;

    // Remove 'www.' if it exists
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }

    return domain;
  } catch (e) {
    console.error('Invalid URL', e);
    return null;
  }
};

type ColumnsContext =
  | 'tp-account-columns'
  | 'tp-contact-columns'
  | 'mdqs-account-columns'
  | 'mdqs-contact-columns'
  | 'web-account-columns'
  | 'web-contact-columns';

export const getUserSelectedTableColumns = (context: ColumnsContext) => {
  const tpAccountColumns = useSelector((state: any) => state.tpAccountColumns);
  const tpContactColumns = useSelector((state: any) => state.tpContactColumns);
  const mdqsAccountColumns = useSelector((state: any) => state.mdqsAccountColumns);
  const mdqsContactColumns = useSelector((state: any) => state.mdqsContactColumns);
  const webAccountColumns = useSelector((state: any) => state.webAccountColumns);
  const webContactColumns = useSelector((state: any) => state.webContactColumns);
  switch (context) {
    case 'tp-account-columns':
      return tpAccountColumns.filter((column: any) => column.checked);
    case 'tp-contact-columns':
      return tpContactColumns.filter((column: any) => column.checked);
    case 'mdqs-account-columns':
      return mdqsAccountColumns.filter((column: any) => column.preferred);
    case 'mdqs-contact-columns':
      return mdqsContactColumns.filter((column: any) => column.preferred);
    case 'web-account-columns':
      return webAccountColumns.filter((column: any) => column.checked);
    case 'web-contact-columns':
      return webContactColumns.filter((column: any) => column.checked);
    default:
      return [];
  }
};

export const extractWebsiteUrl = (url: string | null | undefined): string => {
  if (!url) return 'N/A';

  const trimmedUrl = url.trim();
  if (trimmedUrl === '') return 'N/A';

  // Remove http:// or https:// and www.
  return trimmedUrl
    .replace(/^https?:\/\//i, '') // Remove http:// or https://
    .replace(/^www\./i, ''); // Remove www.
};

export const truncateString = (str: string, maxLength = 20): string => {
  if (str.length <= maxLength) return str;
  return str.length > 0 ? str.slice(0, maxLength) + '...' : '';
};

export const formatPhoneNumber = (phones: string | null): string => {
  if (!phones) return 'N/A';
  const firstPhone = phones.split(',')[0].trim();
  return firstPhone;
};

export const getBaseURL = (url: string) => {
  try {
    const parsedURL = new URL(url);
    return `${parsedURL.protocol}//${parsedURL.hostname}`;
  } catch (error) {
    return '';
  }
};

export const ensureScheme = (url: string) => {
  if (!/^https?:\/\//i.test(url)) {
    return `http://${url}`;
  }
  return url;
};

export function findNameByEmail(arr: any[], email: string) {
  for (const item of arr) {
    if (item.value === email) {
      return item.label;
    }
  }
  return null; // Return null if no match is found
}

function parseNumberFromAbbreviations(value: string): { value: number | null; hasSymbol: boolean } {
  const hasSymbol = value.includes('$');
  value = value.replace('$', '').trim().toUpperCase();

  if (value === 'NONE' || value === '0') return { value: null, hasSymbol };
  if (value.includes('K')) return { value: parseFloat(value.replace('K', '')) * 1_000, hasSymbol };
  if (value.includes('M'))
    return { value: parseFloat(value.replace('M', '')) * 1_000_000, hasSymbol };
  if (value.includes('B'))
    return { value: parseFloat(value.replace('B', '')) * 1_000_000_000, hasSymbol };

  return { value: parseFloat(value), hasSymbol };
}

export const upperValueLimit = (ranges: string[], value: string) => {
  if (!value.includes('None')) {
    return value;
  }
  const lastUpper = ranges[ranges.length - 1].split(' - ')[1];
  const { value: lastUpperNum } = parseNumberFromAbbreviations(lastUpper);
  // If we can't parse the last upper bound, treat it as the maximum.
  const lastUpperNumSafe = lastUpperNum ?? Number.NEGATIVE_INFINITY;

  if (value.includes(' - ')) {
    const [lower, upper] = value.split(' - ').map((v) => v.trim());
    const { value: lowerNum, hasSymbol: lowerHasSymbol } = parseNumberFromAbbreviations(lower);
    const { value: upperNum, hasSymbol: upperHasSymbol } = parseNumberFromAbbreviations(upper);

    const rangeSymbol = '';

    if (lowerNum === null && upperNum === null) {
      return `${rangeSymbol}${lastUpper}+`;
    }

    if (lowerNum === null && upperNum !== null) {
      return `${upper}+`;
    }

    if (upperNum === null && lowerNum !== null) {
      return `${lower}+`;
    }

    if (upperNum !== null && upperNum > lastUpperNumSafe) {
      return `${rangeSymbol}${lastUpper}+`;
    }

    return `${lower} - ${upper}`;
  }

  const { value: numValue, hasSymbol: valueHasSymbol } = parseNumberFromAbbreviations(value);
  const outputSymbol = '';

  if (numValue === null) {
    return `${outputSymbol}${lastUpper}+`;
  }

  if (numValue > lastUpperNumSafe) {
    return `${outputSymbol}${lastUpper}+`;
  }

  return value;
};
export function convertUTCToLocal(utcDateString: string): string {
  // Log the current time for debugging
  const now = dayjs();
  console.log('Current local time:', now.format('YYYY-MM-DD HH:mm:ss'));
  console.log('Input UTC string:', utcDateString);

  // Parse the UTC date string as UTC and convert to local timezone
  // Force parsing as UTC first, then convert to local
  const localDate = dayjs.utc(utcDateString).local();
  console.log('Converted local time:', localDate.format('YYYY-MM-DD HH:mm:ss'));

  // Format the date in the same format as the input
  // This will preserve milliseconds if they were in the original string
  if (utcDateString.includes('.')) {
    // With milliseconds
    return localDate.format('YYYY-MM-DD HH:mm:ss.SSS');
  } else {
    // Without milliseconds
    return localDate.format('YYYY-MM-DD HH:mm:ss');
  }
}

export function convertUTCToIST(utcDateString: string): string {
  if (!utcDateString) return '';

  // Parse and format the UTC date string
  const utcDate = dayjs.utc(utcDateString);

  // Format in Indian locale: DD/MM/YYYY hh:mm:ss A (12-hour format with AM/PM) in UTC
  return utcDate.format('DD/MM/YYYY hh:mm:ss A');
}

export const isValidLink = (link: string): boolean => {
  try {
    const url = new URL(link);
    if (!['http:', 'https:'].includes(url.protocol)) return false;
    const domainRegex = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
    const domain = url.hostname.replace(/^www\./, '');
    if (!domainRegex.test(domain)) return false;

    return true;
  } catch {
    return false;
  }
};

export const capitalizeFirstLetterInAllWordsV2 = (
  input: string | number | null,
): string | number | null => {
  if (typeof input !== 'string') return input;
  return input
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getActualWebsiteUrl = (url: string) => {
  return url.includes('http') ? url : `https://${url}`;
};

export const extractRecommendedIcpData = (data: any) => {
  if (!data) return {};
  const result = {
    flat: {} as Record<string, any>,
  };

  const processData = (obj: any) => {
    Object.entries(obj).forEach(([key, value]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        processData(value);
      } else {
        result.flat[key] = value;
      }
    });
  };
  processData(data);
  return result.flat;
};

export const getICPTopFactors = (data: any[] = [], type: string, limited?: boolean) => {
  const normalizeContribution = (contribution: string) => {
    const normalized = String(contribution || '').toLowerCase();
    if (normalized === 'positive' || normalized === 'high') return 'high';
    if (normalized === 'negative' || normalized === 'low') return 'low';
    return 'medium';
  };
  let factors: any[] = [];
  factors = data;
  if (type === 'low') {
    factors = factors.filter(
      (item) => normalizeContribution(item.contribution) === 'low' && item.name !== 'Intent Topics',
    );
  } else if (type === 'high') {
    factors = factors.filter(
      (item) =>
        normalizeContribution(item.contribution) === 'high' || item.name === 'Intent Topics',
    );
  }
  if (factors.length < 4) {
    return factors;
  } else {
    if (type === 'high' || type === 'low') {
      factors = factors.slice(0, 4);
    } else {
      factors = [...factors.slice(0, 2), ...factors.slice(-2)];
    }
  }

  if (limited && factors.length > 0) {
    let charCount = 0;
    factors.forEach((factor) => {
      const str = Array.isArray(factor.value) ? factor.value[0] : factor.value;
      if (typeof str === 'string') {
        charCount += str.length;
      }
    });

    factors = charCount < 40 ? factors.slice(0, 2) : factors.slice(0, 1);
  }
  return factors;
};

export const getICPRanges = (data: any[] | string | null | undefined = []) => {
  let charCount = 0;
  if (typeof data === 'string') return { showed: 1, ranges: [getICPValue(data)] };
  const safeData = Array.isArray(data) ? data : [];
  safeData.forEach((factor) => {
    if (typeof factor === 'string') {
      charCount += factor.length;
    }
  });
  return {
    showed: (charCount < 23 ? safeData.slice(0, 2) : safeData.slice(0, 1)).length,
    ranges:
      charCount < 23
        ? safeData.slice(0, 2).map((item) => getICPValue(item))
        : safeData.slice(0, 1).map((item) => getICPValue(item)),
  };
};

export const getCharacterCountExcludeHTML = (htmlString: unknown) => {
  const safeString =
    typeof htmlString === 'string' ? htmlString : htmlString == null ? '' : String(htmlString);
  return safeString.replace(/<[^>]*>/g, '').trim().length;
};

export const getTruncatedStringWithoutBreakingLastWord = (str: string, maxLength = 20): string => {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  let truncated = str.slice(0, maxLength);
  if (str[maxLength] !== ' ' && truncated.includes(' ')) {
    truncated = truncated.slice(0, truncated.lastIndexOf(' '));
  }
  return truncated;
};

export const extractDomainFromAnyURLFormat = (input: string) => {
  if (!input) return '';
  let url = input.replace(/(^\w+:|^)\/\//, '');
  url = url.replace(/^www\./, '');
  url = url.replace(/^\//, '');
  const domain = url.split('/')[0];
  return domain;
};

export const formatTimeRemaining = (totalSeconds: number): { value: number; unit: string } => {
  if (totalSeconds < 0) {
    return { value: 0, unit: 'seconds' };
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    // If there are remaining seconds, show them too
    if (seconds > 0) {
      return { value: minutes, unit: 'minutes' };
    }
    return { value: minutes, unit: minutes === 1 ? 'minute' : 'minutes' };
  }

  return { value: seconds, unit: seconds === 1 ? 'second' : 'seconds' };
};

export const formatTimeAsMMSS = (totalSeconds: number): string => {
  if (totalSeconds < 0) return '0:00';

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const convertDateICP = (dateStr: string) => {
  const d = new Date(dateStr);
  const pad = (n: any) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export function generateSecureState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function storeOAuthState(state: string, provider: string): void {
  const stateData = {
    state,
    provider,
    timestamp: Date.now(),
    expiresAt: Date.now() + 10 * 60 * 1000, // 10 minutes expiration
  };
  sessionStorage.setItem('oauth_state', JSON.stringify(stateData));
}

export function validateOAuthState(receivedState: string): boolean {
  try {
    const stored = sessionStorage.getItem('oauth_state');
    if (!stored) {
      console.error('[OAuth Security] No stored OAuth state found');
      return false;
    }

    const stateData = JSON.parse(stored);

    if (Date.now() > stateData.expiresAt) {
      console.error('[OAuth Security] OAuth state expired');
      sessionStorage.removeItem('oauth_state');
      return false;
    }

    if (receivedState !== stateData.state) {
      console.error('[OAuth Security] State mismatch - possible CSRF attack detected');
      return false;
    }

    sessionStorage.removeItem('oauth_state');
    return true;
  } catch (error) {
    console.error('[OAuth Security] Error validating OAuth state:', error);
    return false;
  }
}

export async function generatePKCE(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  const toBase64Url = (buffer: Uint8Array): string => {
    return btoa(
      Array.from(buffer)
        .map((byte) => String.fromCharCode(byte))
        .join(''),
    )
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const codeVerifier = toBase64Url(array);

  const data = new TextEncoder().encode(codeVerifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const codeChallenge = toBase64Url(new Uint8Array(hashBuffer));

  return { codeVerifier, codeChallenge };
}

export function storePKCEVerifier(codeVerifier: string, provider: string): void {
  const pkceData = {
    codeVerifier,
    provider,
    timestamp: Date.now(),
    expiresAt: Date.now() + 10 * 60 * 1000, // 10 minutes
  };
  const providerKey = `pkce_verifier_${provider.toLowerCase()}`;
  sessionStorage.setItem(providerKey, JSON.stringify(pkceData));
  // Backward compatibility for existing consumers
  sessionStorage.setItem('pkce_verifier', JSON.stringify(pkceData));
}

export function getPKCEVerifier(provider?: string): string | null {
  try {
    const providerKey = provider ? `pkce_verifier_${provider.toLowerCase()}` : null;
    const stored =
      (providerKey && sessionStorage.getItem(providerKey)) ||
      sessionStorage.getItem('pkce_verifier');
    if (!stored) return null;

    const pkceData = JSON.parse(stored);

    if (Date.now() > pkceData.expiresAt) {
      console.error('[OAuth Security] PKCE verifier expired');
      if (providerKey) sessionStorage.removeItem(providerKey);
      sessionStorage.removeItem('pkce_verifier');
      return null;
    }

    if (provider && pkceData.provider?.toLowerCase() !== provider.toLowerCase()) {
      console.error('[OAuth Security] PKCE verifier provider mismatch');
      return null;
    }

    if (providerKey) sessionStorage.removeItem(providerKey);
    sessionStorage.removeItem('pkce_verifier');
    return pkceData.codeVerifier;
  } catch (error) {
    console.error('[OAuth Security] Error retrieving PKCE verifier:', error);
    return null;
  }
}

export const transformSelectedRowsForHPM = (selectedRows: any[]) => {
  return selectedRows
    .filter((row) => !row?.isRefreshing)
    .map((row) => {
      return {
        primaryKey: row.primaryKey,
        connectionRequestPk: row.messageActions?.connectionRequestPk ?? null,
        connectionRequestStatus: row.messageActions?.connectionRequestStatus ?? null,
        emailPk: row.messageActions?.emailPk ?? null,
        emailStatus: row.messageActions?.emailStatus ?? null,
        postConnectionRequestPk: row.messageActions?.postConnectionRequestPk ?? null,
        postConnectionRequestStatus: row.messageActions?.postConnectionRequestStatus ?? null,
        followUpPk: row.messageActions?.followUpPk ?? null,
        followUpStatus: row.messageActions?.followUpStatus ?? null,
        secondFollowUpPk: row.messageActions?.secondFollowUpPk ?? null,
        secondFollowUpStatus: row.messageActions?.secondFollowUpStatus ?? null,
        linkedinPostConnectionFollowUpPk: row.messageActions?.linkedinPostConnectionFollowUpPk ?? null,
        linkedinPostConnectionFollowUpStatus: row.messageActions?.linkedinPostConnectionFollowUpStatus ?? null,
        linkedinPostConnectionSecondFollowUpPk: row.messageActions?.linkedinPostConnectionSecondFollowUpPk ?? null,
        linkedinPostConnectionSecondFollowUpStatus: row.messageActions?.linkedinPostConnectionSecondFollowUpStatus ?? null,
        callScriptPk: row.messageActions?.callScriptPk ?? null,
        callScriptMsgPk: row.messageActions?.callScriptMsgPk ?? null,
        callScriptStatus: row.messageActions?.callScriptStatus ?? null,
        followUpCustomPrompt: row.messageActions?.followUpCustomPrompt ?? null,
        secondFollowUpCustomPrompt: row.messageActions?.secondFollowUpCustomPrompt ?? null,
        linkedinPostConnectionFollowUpCustomPrompt: row.messageActions?.linkedinPostConnectionFollowUpCustomPrompt ?? null,
        linkedinPostConnectionSecondFollowUpCustomPrompt: row.messageActions?.linkedinPostConnectionSecondFollowUpCustomPrompt ?? null,
        callScriptCustomPrompt: row.messageActions?.callScriptCustomPrompt ?? null,
        emailCustomPrompt: row.messageActions?.emailCustomPrompt ?? null,
        linkedinConnectionCustomPrompt: row.messageActions?.linkedinConnectionCustomPrompt ?? null,
        linkedinPostCustomPrompt: row.messageActions?.linkedinPostCustomPrompt ?? null,
      };
    });
};

export const hasCmpAccess = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1;
